/**
 * PATH: packages/email-hub/src/feature/components/ComposeAttachmentPreviewModal.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-COMPOSEATTACHMENTPREVIEWMODAL-001
 * PURPOSE: Lightbox preview for the LOCAL `File` objects attached to a
 *          compose draft (PDF → iframe, image → <img>, text → <pre>,
 *          everything else → download / open-in-tab fallback).
 * WHY:     The reader's AttachmentPreviewModal only previews server-side
 *          `EmailAttachment`s (it needs a `download_url`). Compose
 *          attachments are local `File` blobs that never hit the server
 *          until send, so they had no preview at all — the operator
 *          (2026-06-07) couldn't "click and see the pdf in the frame
 *          opening on the attachment". This previews the blob directly
 *          via an object URL, no round-trip, Outlook-style.
 * FLOW:
 *   ┌────────────┐   ┌───────────────┐   ┌──────────────────────┐
 *   │ File blob  │ → │ objectURL +   │ → │ iframe / img / pre /  │
 *   │            │   │ kind(ext/mime)│   │ download fallback     │
 *   └────────────┘   └───────────────┘   └──────────────────────┘
 * RELATED:
 *   - components/ComposeAttachmentStrip.tsx: the host that opens this.
 *   - components/AttachmentPreviewModal.tsx: the server-side sibling.
 */

import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText, X } from 'lucide-react'

export interface ComposeAttachmentPreviewModalProps {
  files: File[]
  initialIndex: number
  onClose: () => void
}

type PreviewKind = 'pdf' | 'image' | 'text' | 'other'

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'])
const TEXT_EXT = new Set(['txt', 'csv', 'json', 'log', 'md', 'xml', 'yaml', 'yml', 'html'])

/** Pure: a `File` → which preview pipeline to use. MIME wins, extension
 *  is the fallback (Outlook routinely ships octet-stream for typed
 *  files). */
export function kindForFile(file: File): PreviewKind {
  const mime = (file.type || '').toLowerCase()
  const ext = (file.name.split('.').pop() || '').toLowerCase()
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (mime.startsWith('image/') || IMAGE_EXT.has(ext)) return 'image'
  if (mime.startsWith('text/') || TEXT_EXT.has(ext)) return 'text'
  return 'other'
}

export default function ComposeAttachmentPreviewModal({
  files,
  initialIndex,
  onClose,
}: ComposeAttachmentPreviewModalProps) {
  const [index, setIndex] = useState(initialIndex)
  useEffect(() => setIndex(initialIndex), [initialIndex])

  const file = files[index]
  const kind = useMemo(() => (file ? kindForFile(file) : 'other'), [file])
  // Re-derived per `file`; the cleanup below revokes the prior URL.
  const url = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file])
  const [text, setText] = useState<string | null>(null)

  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])

  useEffect(() => {
    let cancelled = false
    setText(null)
    if (file && kind === 'text') file.text().then((t) => { if (!cancelled) setText(t) })
    return () => { cancelled = true }
  }, [file, kind])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && index < files.length - 1) setIndex((i) => i + 1)
      if (e.key === 'ArrowLeft' && index > 0) setIndex((i) => i - 1)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [index, files.length, onClose])

  if (!file) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${file.name}`}
    >
      <div
        className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
          <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm text-foreground truncate" title={file.name}>{file.name}</p>
            <p className="text-xs text-muted-foreground">
              {file.type || 'file'}
              {file.size > 0 && ` · ${(file.size / 1024).toFixed(0)} KB`}
              {files.length > 1 && ` · ${index + 1} of ${files.length}`}
            </p>
          </div>
          {files.length > 1 && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                disabled={index === 0}
                className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous (←)"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setIndex((i) => Math.min(files.length - 1, i + 1))}
                disabled={index === files.length - 1}
                className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next (→)"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          <a
            href={url}
            download={file.name}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-accent shrink-0"
            title="Download"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </a>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-accent shrink-0" title="Close (Esc)">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden bg-muted/20">
          {kind === 'pdf' && (
            <iframe src={url} title={file.name} className="w-full h-full border-0 bg-white" />
          )}
          {kind === 'image' && (
            <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
              <img src={url} alt={file.name} className="max-w-full max-h-full object-contain" />
            </div>
          )}
          {kind === 'text' && (
            <pre className="h-full w-full overflow-auto p-4 text-xs whitespace-pre-wrap font-mono bg-background text-foreground">
              {text ?? 'Loading…'}
            </pre>
          )}
          {kind === 'other' && (
            <div className="h-full w-full flex items-center justify-center p-6">
              <div className="max-w-md text-center space-y-4">
                <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center bg-primary/10 text-primary">
                  <FileText className="h-8 w-8" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Inline preview isn't available for this file type yet. Download it or open it in a new tab.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <a
                    href={url}
                    download={file.name}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent"
                  >
                    <Download className="h-4 w-4" /> Download
                  </a>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent"
                  >
                    <ExternalLink className="h-4 w-4" /> Open in new tab
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
